import { apiFetch } from "api/auth";

const API_BASE =
  process.env.REACT_APP_API_URL || "http://localhost:3000/api/v1";

interface ApiSuccessResponse<T> {
  success: true;
  message: string;
  data: T;
}

interface ApiErrorResponse {
  success?: false;
  message?: string;
  error?: string;
}

export type AdminUserRole = "ADMIN" | "STAFF";

export interface AdminUser {
  id: string;
  username: string;
  name: string;
  email: string;
  role: "SUPER_ADMIN" | "ADMIN" | "STAFF" | "AGENT" | "CLIENT";
  isActive: boolean;
  createdAt: string;
  lastLogin?: string | null;
}

export interface CreateAdminUserPayload {
  username: string;
  name: string;
  email: string;
  role: AdminUserRole;
}

function getApiErrorMessage(json: unknown, fallback: string): string {
  if (!json || typeof json !== "object") return fallback;
  const rec = json as ApiErrorResponse;
  return rec.message || rec.error || fallback;
}

export async function listUsers(): Promise<AdminUser[]> {
  const res = await apiFetch(`${API_BASE}/users`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });
  const json = (await res.json()) as
    | ApiSuccessResponse<AdminUser[]>
    | ApiErrorResponse;
  if (!res.ok) throw new Error(getApiErrorMessage(json, "Failed to load users"));
  return (json as ApiSuccessResponse<AdminUser[]>).data ?? [];
}

export async function createUser(payload: CreateAdminUserPayload): Promise<string> {
  const res = await apiFetch(`${API_BASE}/users/add-user`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const json = (await res.json()) as ApiSuccessResponse<unknown> | ApiErrorResponse;
  if (!res.ok) throw new Error(getApiErrorMessage(json, "Failed to create user"));
  return (json as ApiSuccessResponse<unknown>).message || "User created successfully";
}

export async function updateUserStatus(
  userId: string,
  isActive: boolean
): Promise<string> {
  const res = await apiFetch(`${API_BASE}/users/${userId}/status`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ isActive }),
  });
  const json = (await res.json()) as ApiSuccessResponse<unknown> | ApiErrorResponse;
  if (!res.ok) throw new Error(getApiErrorMessage(json, "Failed to update user status"));
  return (json as ApiSuccessResponse<unknown>).message || "User status updated successfully";
}

export async function resendSetupEmail(userId: string): Promise<string> {
  const res = await apiFetch(`${API_BASE}/users/${userId}/resend-setup-email`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
  });
  const json = (await res.json()) as ApiSuccessResponse<unknown> | ApiErrorResponse;
  if (!res.ok) throw new Error(getApiErrorMessage(json, "Failed to resend setup email"));
  return (json as ApiSuccessResponse<unknown>).message || "Setup email sent successfully";
}

export async function deleteUser(userId: string): Promise<string> {
  const res = await apiFetch(`${API_BASE}/users/${userId}`, {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
  });
  const json = (await res.json()) as ApiSuccessResponse<unknown> | ApiErrorResponse;
  if (!res.ok) throw new Error(getApiErrorMessage(json, "Failed to delete user"));
  return (json as ApiSuccessResponse<unknown>).message || "User deleted successfully";
}
